import React, { useState } from 'react';
import { getCsrfToken } from '../../lib/csrf.js';
import { Link } from '@inertiajs/react';
import Layout from '../../components/Layout.jsx';
import PaymentForm from '../../components/PaymentForm.jsx';
import ReviewCards from '../../components/ReviewCards.jsx';
import {
  INDIVIDUAL_VARIANTS, ROLE_LABELS, TRAINING_LABELS, FACILITY_TYPE_CODES, variantKey, formatPrice,
} from '../../constants/reviewVariants.js';

export default function New({ role, training_type, facility_type, pricing, publishable_key }) {
  const [plan, setPlan] = useState('subscription');
  const [session, setSession] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const currentVariant = role && training_type ? INDIVIDUAL_VARIANTS[variantKey(role, training_type)] : null;
  const anyPlanAvailable = !!(pricing?.subscription || pricing?.one_time);

  const handleContinue = async () => {
    if (submitting || !anyPlanAvailable) return;
    setSubmitting(true);
    setError(null);
    try {
      const csrfToken = getCsrfToken();
      const response = await fetch('/student_purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ plan }),
      });
      const data = await response.json();
      if (response.ok && data.client_secret) {
        setSession({
          clientSecret:   data.client_secret,
          publishableKey: data.publishable_key || publishable_key,
          returnUrl:      data.return_url,
          mode:           data.mode,
          amountCents:    data.amount_cents,
        });
      } else {
        setError(data.error || 'Could not start checkout.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-12 px-6 lg:px-8">
        <div className="max-w-2xl mx-auto space-y-6">
          {!session ? (
            <>
              <h2 className="text-center text-2xl/9 font-bold tracking-tight text-gray-900">Choose your training plan</h2>

              <div className="bg-white rounded-lg border border-gray-200 p-5 divide-y divide-gray-100">
                <div className="flex justify-between py-2 text-sm">
                  <span className="text-gray-500">Role</span>
                  <span className="font-medium text-gray-900">{ROLE_LABELS[role]}</span>
                </div>
                <div className="flex justify-between py-2 text-sm">
                  <span className="text-gray-500">Training</span>
                  <span className="font-medium text-gray-900">{TRAINING_LABELS[training_type]}</span>
                </div>
                <div className="flex justify-between py-2 text-sm">
                  <span className="text-gray-500">Facility Type</span>
                  <span className="font-medium text-gray-900">{FACILITY_TYPE_CODES[facility_type]}</span>
                </div>
              </div>

              {currentVariant && (
                <ReviewCards
                  variant={currentVariant} pricing={pricing} loading={false}
                  selectedPlan={plan} onSelectPlan={setPlan}
                  complianceText={pricing?.subscription?.compliance_text ?? pricing?.one_time?.compliance_text}
                  facilityCode={FACILITY_TYPE_CODES[facility_type]}
                />
              )}

              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>
              )}

              <div className="flex gap-3 pt-2">
                <Link href="/" className="flex-1 text-center rounded-lg bg-white px-5 py-2.5 text-sm/6 font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200 hover:bg-gray-50 cursor-pointer">
                  Back
                </Link>
                <button type="button" onClick={handleContinue} disabled={submitting || !anyPlanAvailable}
                  className="flex-1 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-indigo-700 cursor-pointer disabled:opacity-50">
                  {submitting ? 'Preparing checkout…' : 'Continue to payment'}
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl/9 font-bold tracking-tight text-gray-900">Complete your purchase</h2>
              <p className="text-sm text-gray-600">Securely complete payment below. You won't leave this page.</p>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-white p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-800">Order summary</h3>
                      <button type="button" onClick={() => setSession(null)}
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-700 cursor-pointer">
                        Edit
                      </button>
                    </div>
                    <div className="text-sm space-y-1">
                      <p className="font-medium text-gray-900">{ROLE_LABELS[role]}</p>
                      <p className="text-xs text-gray-500">{TRAINING_LABELS[training_type]}</p>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
                      <span className="text-sm font-semibold text-gray-900">{session.mode === 'subscription' ? 'Due today' : 'Total'}</span>
                      <span className="text-lg font-bold text-indigo-600">{formatPrice(session.amountCents)}</span>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-3">
                  <div className="rounded-lg border border-gray-200 bg-white p-5">
                    <PaymentForm
                      clientSecret={session.clientSecret}
                      publishableKey={session.publishableKey}
                      returnUrl={session.returnUrl}
                      submitLabel={`Pay ${formatPrice(session.amountCents)}`}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
